'use client'

import type { ColumnDef } from '@tanstack/react-table'
import { Loader2Icon, MessageSquareShareIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDeleteNote } from '@/api/hooks/useDeleteNote'
import type { NoteGenerationStatus } from '@/api/hooks/useNoteGenerationStatus'
import type { Note } from '@/api/hooks/useNotes'
import { noteDeleteToast } from '@/components/molecules/custom-async-toasts'
import { ColumnActions } from '@/components/organisms/data-table/actions-column'
import { Button } from '@/components/ui/button'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useIndividuals } from '@/hooks/useIndividuals'
import { DateUtils } from '@/lib/utils'
import { useAppDispatch } from '@/redux/hooks'
import { AssistantActions } from '@/redux/reducers/assistant'

import { ConfirmDialog } from '../confirm-dialog'

export const useNotesTableColumns: (
  notes: Note[],
  statuses: Record<string, NoteGenerationStatus>,
) => ColumnDef<Note>[] = (_notes, statuses) => {
  const { selectedIndividualId } = useIndividuals()
  const deleteNote = useDeleteNote()
  const { t, i18n } = useTranslation()

  const onRequestDeleteDocument = (id: string) => {
    if (!selectedIndividualId) return
    const deletionPromise = deleteNote.mutateAsync({
      individualId: selectedIndividualId,
      noteId: id,
    })
    noteDeleteToast(deletionPromise)
  }

  return [
    {
      accessorKey: 'title',
      cell: ({ row }) => {
        const isGenerating = statuses[row.original.id] === 'generating'
        const title = row.original.title

        if (isGenerating && !title) {
          return (
            <span className="flex items-center gap-2 text-text-secondary">
              <Loader2Icon className="size-4 animate-spin" />
              {t('notesTable.generating')}
            </span>
          )
        }

        return (
          <div>
            <span className="font-medium line-clamp-1 max-w-prose">
              {title || t('notesTable.untitled')}
            </span>
            {row.original.updatedAt && (
              <div className="@lg/table:hidden mt-2 text-xs text-text-secondary font-medium">
                {DateUtils.formatDayAndTimeDate(new Date(row.original.updatedAt))}
              </div>
            )}
          </div>
        )
      },
      header: t('notesTable.columns.title'),
      // Large finite width so auto table-layout gives the title column the slack
      // and shrinks the trailing columns to their content on the right edge.
      // (Number.POSITIVE_INFINITY serializes to an invalid width and is ignored.)
    },
    {
      accessorKey: 'updatedAt',
      cell: ({ cell }) => {
        const date = cell.getValue<string>() ? new Date(cell.getValue<string>()) : new Date()

        return (
          <div className="text-text-secondary text-right whitespace-nowrap">
            {DateUtils.formatRelativeDate(date, t)}
          </div>
        )
      },
      columnClassName: 'hidden @lg/table:table-cell',
      header: () => <div className="text-right">{t('global.updatedAt')}</div>,
    },
    {
      cell: ({ row }) => {
        const [deleteOpen, setDeleteOpen] = useState(false)
        const dispatch = useAppDispatch()

        const openRelatedThread = () => {
          if (!row.original.relatedToThread) return

          dispatch(AssistantActions.setActiveThreadId(row.original.relatedToThread))
          dispatch(AssistantActions.toggleSideChat(true))
        }

        return (
          <ColumnActions
            dropDownMenuContent={
              <DropdownMenuItem destructive onSelect={() => setDeleteOpen(true)}>
                {t('notesTable.rowActionsDropdown.deleteNote')}
              </DropdownMenuItem>
            }
            showClickableButton={false}
          >
            {row.original.relatedToThread && (
              <div className="w-8" onClick={e => e.stopPropagation()}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="hover:bg-layer-1"
                      onClick={openRelatedThread}
                      size="icon"
                      variant="ghost"
                    >
                      <MessageSquareShareIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('notesTable.rowActionsDropdown.openRelatedConversation')}
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
            <ConfirmDialog
              onConfirm={() => onRequestDeleteDocument(row.original.id)}
              onOpenChange={setDeleteOpen}
              open={deleteOpen}
              variant="destructive"
            />
          </ColumnActions>
        )
      },
      columnClassName: 'w-px p-2 @3xl/table:p-5',
      enableHiding: false,
      id: 'actions',
    },
  ]
}
